import {
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useState
} from "react";

import { Heading } from "@magnetic/heading";
import { Spinner } from "@magnetic/spinner";
import { Text } from "@magnetic/text";
import { TextArea } from "@magnetic/text-area";

import { Button } from "src/components/buttons";
import { ErrorNotification } from "src/components/errorNotification";
import { HFlex } from "src/components/hFlex";
import { VFlex } from "src/components/vFlex";
import { ApiError } from "src/core";

import "./commentModal.scss";

const MAX_COMMENT_LENGTH = 2000;

interface CommitCandidateFormProps {
  readonly error?: ApiError;
  readonly onClose: () => void;
  readonly onCommit: (comment: string) => Promise<void>;
  readonly entityName?: string;
}

export const CommitCandidateForm = (props: CommitCandidateFormProps) => {
  const { onClose, onCommit, entityName = "configuration", error } = props;
  const [comment, setComment] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const charLimitExceeded = comment.length > MAX_COMMENT_LENGTH;

  const pushCfg = useCallback(() => {
    if (comment) {
      setLoading(true);
      onCommit(comment.trim())
        .then(() => {
          onClose();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      onClose();
    }
  }, [comment, onCommit, onClose]);

  const onChange = useCallback((e: FormEvent<HTMLTextAreaElement>) => {
    setComment(e.currentTarget.value);
  }, []);

  /**
   * TORT-24234 (validating TORT-18076): typing Space in this TextArea was
   * closing the popover in Chrome.
   *
   * The TextArea lives inside the "Comment and push" <Button> that owns the
   * popover (the form is rendered as the button's children). Space is the
   * browser's default *activation* key for a button, so on key-up Chrome
   * fires the button's synthetic click and toggles the popover shut.
   *
   * preventDefault() is what actually fixes this: it cancels that default
   * button-activation action. It does NOT affect the typed character — the
   * space/newline is inserted on key-down, so by key-up it is already in the
   * TextArea value. stopPropagation() alone does not help, because the button
   * activation is a browser default action, not a bubbled listener; we keep
   * it only to prevent the key-up from reaching any ancestor key handlers.
   */
  const onKeyup = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const cancelEvent = useCallback((e: MouseEvent) => {
    /**
     * Need to stop the click events from propagating to the parent
     * button. it will close the popover when the user tries
     * to click on anything in the form
     */
    e.stopPropagation();
  }, []);

  return (
    <VFlex className="comment-and-push-form" onClick={cancelEvent}>
      <HFlex
        align="center"
        gap="sm"
        margin={{
          bottom: 16
        }}
      >
        <Heading as="h2" size="primary">
          Comment before pushing configuration
        </Heading>
      </HFlex>
      <Text
        size="p2"
        style={{
          marginBottom: 24
        }}
      >
        You are about to push changes to {entityName} that may disrupt your
        current network performance and activity. Leave a comment that describes
        what changes you are making and why.
      </Text>
      <TextArea
        name="comment"
        label="Comment"
        required={true}
        onChange={onChange}
        onKeyUp={onKeyup}
        value={comment}
      />
      <ErrorNotification
        error={error}
        msg={
          charLimitExceeded
            ? `Comment cannot exceed ${MAX_COMMENT_LENGTH.toLocaleString()} characters`
            : null
        }
      />
      <HFlex justify="flex-end">
        <Button.Group>
          <Button
            name="modal-cancel"
            kind="tertiary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={pushCfg}
            type="submit"
            name="modal-submit"
            disabled={comment.trim().length < 3 || charLimitExceeded || loading}
            icon={loading ? <Spinner size="xs" /> : null}
          >
            Push configuration
          </Button>
        </Button.Group>
      </HFlex>
    </VFlex>
  );
};
